import { useIsWhiteLabel } from '@/components/app/providers/whiteLabelProvider/WhiteLabelProvider';
import { convertUrlToParamsObject, toEnglish, toPersian } from '@/helpers/props';
import { digitsEnToFa } from '@persian-tools/persian-tools';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { FC, useMemo } from 'react';
import { generateMetadata } from './constants';
interface SEOMetaDataProps {
  data: any;
  perPage: number;
  host: string;
  pageContent: 'cottage' | 'boomgardi';
}
const SEOMetaData: FC<SEOMetaDataProps> = ({
  data,
  perPage,
  host,
  pageContent
}) => {
  const router = useRouter();
  const isWhiteLabel = useIsWhiteLabel();
  const defaultMinPrice = data?.min_price?.amount ? digitsEnToFa(data?.min_price?.amount) : '';
  const calculatedPrice = (+toEnglish(defaultMinPrice) / 1000).toString();
  const minPrice = calculatedPrice.length > 3 ? `${toPersian(parseFloat((+calculatedPrice / 1000).toFixed(1)))} میلیون` : `${toPersian(calculatedPrice)} هزار`;
  const houseCount = data?.pagination.total && data?.pagination.total > 1 ? ` بین ${toPersian(data?.pagination.total)}` : '';
  const href = isWhiteLabel ? `${host}${router.asPath}` : `https://www.shab.ir${router.asPath}`;
  const pageQuery = convertUrlToParamsObject(href)?.page || 1;
  const urlWithoutPageNext = new URL(href);
  const urlWithoutPagePrev = new URL(href);
  const nextPage = pageQuery ? +pageQuery + 1 : 1;
  const prevPage = +pageQuery - 1;
  const mainTotal = Number(Math.ceil(data.pagination?.total / perPage)) || 0;
  const nextExists = +pageQuery < mainTotal;
  urlWithoutPageNext.searchParams.delete('page');
  urlWithoutPagePrev.searchParams.delete('page');
  urlWithoutPageNext.searchParams.append('page', nextPage.toString());
  if (+prevPage > 1) {
    urlWithoutPagePrev.searchParams.append('page', prevPage.toString());
  }
  const metadataSet = generateMetadata({
    minPrice,
    houseCount
  })[pageContent];
  const title = metadataSet.title;
  const description = metadataSet.description;
  const breadcrumb = metadataSet.breadcrumb;
  let canonical = metadataSet.canonical;
  if (router.query.page) {
    canonical += `?page=${router.query.page}`;
  }
  const image = `https://www.shab.ir/images/home/hero/hero.webp`;
  const faqSchema = useMemo(() => {
    if (!data?.region_data?.faqs) return '';
    return `{"@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity":[${data?.region_data?.faqs.map(item => `
        {
          "@type": "Question",
          "name": "${item.question}",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "${item.answer}"
          }
        }
        `)}]
      }`;
  }, [data?.region_data?.faqs]);
  return <Head data-sentry-element="Head" data-sentry-component="SEOMetaData" data-sentry-source-file="SEOMetaData.tsx">
      <title>{title}</title>{' '}
      {!isWhiteLabel && <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: breadcrumb
    }} />}
      <meta name="description" content={description} data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta property="og:title" content={title} data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta property="og:description" content={description} data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta property="og:url" content={canonical} data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta property="og:site_name" content={isWhiteLabel ? '' : 'سایت شب'} data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta property="og:type" content="website" data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta property="og:image" content={image} data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta name="twitter:card" content="summary_large_image" data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta name="twitter:site" content="@shabhost" data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta name="twitter:title" content={title} data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta name="twitter:description" content={description} data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      <meta name="twitter:image:src" content={image} data-sentry-element="meta" data-sentry-source-file="SEOMetaData.tsx" />
      {nextExists && <link rel="next" href={urlWithoutPageNext.href} />}
      {prevPage > 0 && +pageQuery <= mainTotal + 1 && <link rel="prev" href={urlWithoutPagePrev.href} />}
      {!isWhiteLabel && <link rel="canonical" href={canonical} />}
      {!isWhiteLabel && faqSchema ? <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: faqSchema
    }}></script> : null}
    </Head>;
};
export default SEOMetaData;